<script>
import { defineComponent, reactive, ref, onMounted, onUnmounted } from 'vue'

const NORMAL_WIDTH = 1700
const NORMAL_HEIGHT = 2000
const GUTTERS = 16

export default defineComponent({
  props: {
    hall: { type: Object, required: true },
    poster: {
      type: String,
      required: true
    },
    placeComponent: { type: Object, required: true },
    popupComponent: { type: Object, required: true },
    tickets: { type: Array, default: () => [] },
    cart: { type: Array, default: () => [] }
  },
  emits: ['addTicketToCart', 'removeTicketFromCart'],
  methods: {
    onAddTicketToCart(e) {
      this.$emit('addTicketToCart', e)
    },

    onRemoveTicketFromCart(e) {
      this.$emit('removeTicketFromCart', e)
    }
  },
  setup(props) {
    const hall = reactive(props.hall)

    const wrapper = ref(null)

    const zoom = ref(1)
    const maxHeight = ref('-webkit-fill-available')

    const onWindowResize = () => {
      const width = window.innerWidth
      if (width < NORMAL_WIDTH + 2 * GUTTERS) {
        zoom.value = width / (GUTTERS * 2 + NORMAL_WIDTH)
        maxHeight.value = `${zoom.value * NORMAL_HEIGHT + 25}px`
      } else {
        zoom.value = 1
        maxHeight.value = `${zoom.value * NORMAL_HEIGHT + 25}px`
      }
    }
    onMounted(() => {
      window.addEventListener('resize', onWindowResize)
      onWindowResize()
    })

    onUnmounted(() => {
      window.removeEventListener('resize', onWindowResize)
    })
    return {
      zoom,
      maxHeight,
      wrapper
    }
  }
})
</script>

<template>
  <div
    class="max-w-full w-full relative flex flex-row justify-center"
    ref="wrapper"
    :style="{ height: `${maxHeight}` }"
  >
    <div class="origin-top flex-auto" :style="`transform: scale(${zoom});`">
      <div class="w-full relative flex flex-col space-y-9">
        <div class="hall gap-3">
          <div
            class="h-28 w-full bg-dark-gray flex rounded-lg"
            style="grid-area: scene"
          >
            <p class="m-auto text-xl2 text-white font-sans">Сцена</p>
          </div>
          <template v-for="(row, rowKey) in hall" :key="`row-${rowKey}`">
            <component
              v-for="(place, placeKey) in row"
              :key="`row-${rowKey}, place-${placeKey}`"
              :is="placeComponent"
              :popup-component="popupComponent"
              :row="rowKey"
              :hall-category="hall.cathegory"
              :place-key="placeKey"
              :place="hall[rowKey][placeKey]"
              :amphitheater="true"
              :parther="false"
              :tickets="tickets"
              :cart="cart"
              @add-ticket-to-cart="onAddTicketToCart"
              @remove-ticket-from-cart="onRemoveTicketFromCart"
              :style="`grid-area: r${rowKey}p${placeKey}`"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.hall {
  display: grid;
  grid-template-columns: repeat(24, minmax(36px, 1fr));
  grid-template-rows: repeat(43, minmax(36px, 1fr));
  justify-content: center;

  grid-template-areas:
    '. . . scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene'
    '. . . scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene'
    'r19p1 . . scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene'
    'r19p2 . . scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene'
    'r19p3 . . scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene scene'
    'r19p4 . . . . . . . . . . . . . . . . . . . . . . .'
    'r19p5 . . . . . . . . . . . . . . . . . . . . . . .'
    'r19p6 . r1p1 r1p2 r1p3 r1p4 r1p5 r1p6 r1p7 r1p8 r1p9 r1p10 r1p11 r1p12 r1p13 r1p14 r1p15 r1p16 r1p17 r1p18 r1p19 r1p20 r1p21 r1p22'
    'r19p7 . . . . . . . . . . . . . . . . . . . . . . .'
    'r19p8 . r2p1 r2p2 r2p3 r2p4 r2p5 r2p6 r2p7 r2p8 r2p9 r2p10 r2p11 r2p12 r2p13 r2p14 r2p15 r2p16 r2p17 r2p18 r2p19 r2p20 r2p21 r2p22'
    'r19p9 . . . . . . . . . . . . . . . . . . . . . . .'
    'r19p10 . r3p1 r3p2 r3p3 r3p4 r3p5 r3p6 r3p7 r3p8 r3p9 r3p10 r3p11 r3p12 r3p13 r3p14 r3p15 r3p16 r3p17 r3p18 r3p19 r3p20 r3p21 r3p22'
    'r19p11 . . . . . . . . . . . . . . . . . . . . . . .'
    'r19p12 . r4p1 r4p2 r4p3 r4p4 r4p5 r4p6 r4p7 r4p8 r4p9 r4p10 r4p11 r4p12 r4p13 r4p14 r4p15 r4p16 r4p17 r4p18 r4p19 r4p20 r4p21 r4p22'
    'r19p13 . . . . . . . . . . . . . . . . . . . . . . .'
    'r19p14 . r5p1 r5p2 r5p3 r5p4 r5p5 r5p6 r5p7 r5p8 r5p9 r5p10 r5p11 r5p12 r5p13 r5p14 r5p15 r5p16 r5p17 r5p18 r5p19 r5p20 r5p21 r5p22'
    'r19p15 . . . . . . . . . . . . . . . . . . . . . . .'
    'r19p16 . r6p1 r6p2 r6p3 r6p4 r6p5 r6p6 r6p7 r6p8 r6p9 r6p10 r6p11 r6p12 r6p13 r6p14 r6p15 r6p16 r6p17 r6p18 r6p19 r6p20 r6p21 r6p22'
    'r19p17 . . . . . . . . . . . . . . . . . . . . . . .'
    'r19p18 . . . r7p1 r7p2 r7p3 r7p4 r7p5 r7p6 r7p7 r7p8 r7p9 r7p10 r7p11 r7p12 r7p13 r7p14 r7p15 r7p16 r7p17 r7p18 r7p19 r7p20'
    '. . . . . . . . . . . . . . . . . . . . . . . .'
    'r20p1 . . . . . . . r8p1 r8p2 r8p3 r8p4 r8p5 r8p6 r8p7 r8p8 r8p9 r8p10 r8p11 r8p12 r8p13 r8p14 r8p15 r8p16'
    'r20p2 . . . . . . . . . . . . . . . . . . . . . . .'
    'r20p3 . . . . . . . r9p1 r9p2 r9p3 r9p4 r9p5 r9p6 r9p7 r9p8 r9p9 r9p10 r9p11 r9p12 r9p13 r9p14 r9p15 r9p16'
    'r20p4 . . . . . . . . . . . . . . . . . . . . . . .'
    'r20p5 . . . . . . r10p1 r10p2 r10p3 r10p4 r10p5 r10p6 r10p7 r10p8 r10p9 r10p10 r10p11 r10p12 r10p13 r10p14 r10p15 r10p16 r10p17'
    'r20p6 . . . . . . . . . . . . . . . . . . . . . . .'
    'r20p7 . . . . . . . . . . . . . . . . . . . . . . .'
    'r20p8 . . . . . . r11p1 r11p2 r11p3 r11p4 r11p5 r11p6 r11p7 r11p8 r11p9 r11p10 r11p11 r11p12 r11p13 r11p14 r11p15 r11p16 r11p17'
    'r20p9 . . . . . . . . . . . . . . . . . . . . . . .'
    'r20p10 . . . . . . r12p1 r12p2 r12p3 r12p4 r12p5 r12p6 r12p7 r12p8 r12p9 r12p10 r12p11 r12p12 r12p13 r12p14 r12p15 r12p16 r12p17'
    'r20p11 . . . . . . . . . . . . . . . . . . . . . . .'
    'r20p12 . . . . . . . r13p1 r13p2 r13p3 r13p4 r13p5 r13p6 r13p7 r13p8 r13p9 r13p10 r13p11 r13p12 r13p13 r13p14 r13p15 r13p16'
    '. . . . . . . . . . . . . . . . . . . . . . . .'
    '. . . . . . . . r14p1 r14p2 r14p3 r14p4 r14p5 r14p6 r14p7 r14p8 r14p9 r14p10 r14p11 r14p12 r14p13 r14p14 r14p15 r14p16'
    '. . . . . . . . . . . . . . . . . . . . . . . .'
    '. . . . . . . r15p1 r15p2 r15p3 r15p4 r15p5 r15p6 r15p7 r15p8 r15p9 r15p10 r15p11 r15p12 r15p13 r15p14 r15p15 r15p16 r15p17'
    '. . . . . . . . . . . . . . . . . . . . . . . .'
    '. . . . . . . r16p1 r16p2 r16p3 r16p4 r16p5 r16p6 r16p7 r16p8 r16p9 r16p10 r16p11 r16p12 r16p13 r16p14 r16p15 r16p16 r16p17'
    '. . . . . . . . . . . . . . . . . . . . . . . .'
    '. . . . . . . r17p1 r17p2 r17p3 r17p4 r17p5 r17p6 r17p7 r17p8 r17p9 r17p10 r17p11 r17p12 r17p13 r17p14 r17p15 r17p16 r17p17'
    '. . . . . . . . . . . . . . . . . . . . . . . .'
    '. . . . . . . . r18p1 r18p2 r18p3 r18p4 r18p5 r18p6 r18p7 r18p8 r18p9 r18p10 r18p11 r18p12 r18p13 r18p14 r18p15 r18p16';
}
</style>
